import { formatRunListTimestamp, getRunTriggerLabel } from '../../helpers/sisSyncRunDisplay'
import { type ISisSyncRun, type SisSyncRunOutcome, type SisSyncRunTrigger } from '../../models/SisSyncRun'

// Chip, labels and timestamp format come from the shared helper, so the list and the run detail
// page read a run the same way.

// Skipped is normal for files a school never sends, so the line informs rather than alarms.
const getSkippedFilesLine = (filesSkipped: number | undefined): string | null => {
  if (filesSkipped == null || filesSkipped <= 0) return null
  return filesSkipped === 1
    ? '1 expected file was not received. Nothing was imported from it.'
    : `${filesSkipped} expected files were not received. Nothing was imported from them.`
}

/**
 * The runs list's Files cell: the file total, plus the most notable non-zero count. Failed outranks
 * with errors, which outranks skipped.
 */
export const getRunFilesSummary = (run: Pick<ISisSyncRun, 'filesTotal' | 'filesFailed' | 'filesWithErrors' | 'filesSkipped'>): string => {
  const total = run.filesTotal.toLocaleString()
  if (run.filesFailed > 0) return `${total} (${run.filesFailed.toLocaleString()} failed)`
  if (run.filesWithErrors > 0) return `${total} (${run.filesWithErrors.toLocaleString()} with errors)`
  if (run.filesSkipped > 0) return `${total} (${run.filesSkipped.toLocaleString()} skipped)`
  return total
}

/**
 * Hover lines for a run row, in display order. Only lines with a value are returned.
 * The failure message comes first and is shown exactly as the backend sent it.
 */
export const getRunTooltipLines = (run: Partial<ISisSyncRun>): string[] => {
  const lines: Array<string | null> = [
    run.failureMessage ? run.failureMessage : null,
    run.outcome === 'RUNNING' && run.currentFileName ? `Current file: ${run.currentFileName}` : null,
    run.outcome === 'RUNNING' && run.lastProgressOn ? `Last progress: ${formatRunListTimestamp(run.lastProgressOn)}` : null,
    run.trigger ? `Trigger: ${getRunTriggerLabel(run.trigger)}` : null,
    getSkippedFilesLine(run.filesSkipped),
    run.workerName
      ? `Worker: ${run.workerName}${run.workerProcess ? ` (${run.workerProcess})` : ''}`
      : null,
    run.celeryTaskId ? `Celery task: ${run.celeryTaskId}` : null,
    run.parentTaskId ? `Parent task: ${run.parentTaskId}` : null,
    run.enqueuedBy ? `Enqueued by: ${run.enqueuedBy}` : null
  ]
  return lines.filter((line): line is string => line != null)
}

export interface SisSyncRunFilterValues {
  outcome: SisSyncRunOutcome | ''
  trigger: SisSyncRunTrigger | ''
  // yyyy-MM-dd from a date input, in the viewer's own calendar.
  startedFrom: string
  startedTo: string
}

export const EMPTY_FILTERS: SisSyncRunFilterValues = { outcome: '', trigger: '', startedFrom: '', startedTo: '' }

// Local midnight of a yyyy-MM-dd date, as a UTC ISO string.
const localDayStartUtc = (day: string, addDays = 0): string => {
  const [year, month, date] = day.split('-').map(Number)
  return new Date(year, month - 1, date + addDays).toISOString()
}

/**
 * The `filters` JSON the runs endpoint reads: a flat list of `{ field, op, value }`, ANDed.
 * Field names are the backend's snake_case names, because the case converter does not reach
 * inside the string. The To date is inclusive, so it becomes `< start of the next day`.
 * Returns undefined when nothing is set, so no `filters` param is sent.
 */
export const buildRunFilters = (values: SisSyncRunFilterValues): string | undefined => {
  const filters: Array<{ field: string, op: string, value: string }> = []
  if (values.outcome) filters.push({ field: 'outcome', op: '==', value: values.outcome })
  if (values.trigger) filters.push({ field: 'trigger', op: '==', value: values.trigger })
  if (values.startedFrom) filters.push({ field: 'started_on', op: '>=', value: localDayStartUtc(values.startedFrom) })
  if (values.startedTo) filters.push({ field: 'started_on', op: '<', value: localDayStartUtc(values.startedTo, 1) })
  return filters.length > 0 ? JSON.stringify(filters) : undefined
}
